import { useQueries } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { env } from "../utils/env";
import { monitoringActivityQueryKey } from "./useMonitoringActivityView";

/**
 * Every Screening activity's contribution, fetched together.
 *
 * The page needs all of them, not just the one on screen. The activity payload
 * is the only source for a match's name, its evidence, the run history line and
 * the result counts on the timeline, because the accumulated match list carries
 * none of that (LNXS-S218 will change this). Fetching lazily on selection would
 * leave those blank until the officer happened to click through every alert.
 *
 * Shares its query key with useMonitoringActivityView, so opening the same
 * activity in the legacy slider costs nothing.
 */

// A record monitored daily for a year would otherwise issue hundreds of
// requests on mount. Twelve covers a year of monthly monitoring, which is the
// common case; the page states how many were left out rather than truncating
// quietly.
export const ACTIVITY_PAYLOAD_LIMIT = 12;

const fetchActivity = (authHeader: string, activityId: number) => () =>
  axios.get(
    `${env.verifyServiceUrl}individual/monitoring_activity/${activityId}/`,
    { headers: { Authorization: authHeader } },
  );

export const useMonitoringActivityPayloads = (
  activityIds: number[],
  limit: number = ACTIVITY_PAYLOAD_LIMIT,
) => {
  const { authHeader } = useAuth();
  const loadedIds = activityIds.slice(0, limit);

  const results = useQueries({
    queries: loadedIds.map((activityId) => ({
      queryKey: monitoringActivityQueryKey(activityId),
      queryFn: fetchActivity(authHeader, activityId),
      retry: false,
      // an activity payload never changes once written, so there is no reason
      // to refetch one the officer has already looked at
      staleTime: Infinity,
    })),
  });

  return {
    /** One entry per loaded activity, in the order the ids were given. */
    payloads: results.map((result, index) => ({
      activityId: loadedIds[index],
      data: (result.data as any)?.data,
      isLoading: result.isLoading,
      isError: result.isError,
      refetch: result.refetch,
      /** Changes on every refetch, not just the first load — the signal a
       * consumer needs to notice a payload was invalidated and re-fetched
       * with a new match decision or approval comment. */
      dataUpdatedAt: result.dataUpdatedAt,
    })),
    /** True until every payload has resolved one way or the other. */
    isLoading: results.some((result) => result.isLoading),
    /** Activities beyond the limit, which the page must declare. */
    notLoadedCount: Math.max(0, activityIds.length - loadedIds.length),
  };
};
